import { TAI_LOGO_SRC } from './logo';
import type { HistoryMessage } from './api';
import { OrchestratorClient } from './api';
import { clearConversationCookie, readConversationCookie, writeConversationCookie } from './cookies';
import { disableHostSplit, enableHostSplit, removeHostLayoutStyles } from './host-layout';
import { applyPaletteColors, PRESET_PALETTE_OPTIONS, resolvePalette } from './palette';
import { readPageUrl } from './page-context';
import { createProcessingStatus } from './processing-status';
import { escapeHtml, renderRichText } from './rich-text';
import { widgetStyles } from './styles';
import type {
  ChatResponse,
  DomainOffer,
  OrcaChatWidgetConfig,
  OrcaChatWidgetInstance,
  RecoveryChannelOption,
  SessionResponse,
  WidgetTheme,
} from './types';

const DEFAULT_TITLE = 'TAI - Turbify AI Assistant';
const DEFAULT_SUBTITLE = 'How can I help you today?';
const DEFAULT_ASSISTANT_NAME = 'TAI';

const STORAGE_KEY = 'orca-chat-conversation-id';
const FULLSCREEN_STORAGE_KEY = 'orca-chat-panel-fullscreen';
const THEME_STORAGE_KEY = 'orca-chat-theme';
const PANEL_OPEN_STORAGE_KEY = 'orca-chat-panel-open';
const PALETTE_STORAGE_KEY = 'orca-chat-palette';
const TRANSCRIPT_KEY_PREFIX = 'orca-chat-transcript:';
const LATEST_TRANSCRIPT_KEY = 'orca-chat-transcript:latest';
const MAX_STORED_MESSAGES = 200;

type ResolvedTheme = 'light' | 'dark';
type MessageRole = 'user' | 'assistant' | 'system' | 'error';

function isMessageRole(value: unknown): value is MessageRole {
  return value === 'user' || value === 'assistant' || value === 'system' || value === 'error';
}

function isWidgetTheme(value: string | null): value is WidgetTheme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function resolveTheme(theme: WidgetTheme): ResolvedTheme {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

const CHANNEL_LABELS: Record<string, string> = {
  primary_email: 'Primary email',
  phone_number: 'Phone number',
  alternate_email: 'Alternate email',
};

function channelButtonLabel(channel: RecoveryChannelOption): string {
  const label = CHANNEL_LABELS[channel.channelRef] ?? channel.channelRef.replace(/_/g, ' ');
  return channel.maskedValue ? `${label} (${channel.maskedValue})` : label;
}

function formatResponse(response: ChatResponse): string {
  if (response.message) {
    return response.message;
  }

  if (response.requiresConfirmation && response.confirmationMessage) {
    return response.confirmationMessage;
  }

  if (response.status === 'COMPLETED') {
    const workflow = response.workflowId ?? 'workflow';
    const result = response.state?.recoveryResult as { message?: string } | undefined;
    if (result?.message) {
      return result.message;
    }
    return `${workflow} completed successfully.`;
  }

  if (response.helpArticle?.answer) {
    return `${response.helpArticle.title ?? 'Help'}\n\n${response.helpArticle.answer}`;
  }

  return 'Request processed.';
}

/** Resolves orchestrator navigate targets to a safe absolute URL on the current origin when relative. */
function resolveNavigateUrl(target: string): string | null {
  const trimmed = target.trim();
  if (!trimmed) {
    return null;
  }

  try {
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return new URL(trimmed, window.location.origin).href;
    }

    const url = new URL(trimmed, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

export function createWidget(config: OrcaChatWidgetConfig): OrcaChatWidgetInstance {
  const orchestratorUrl = config.orchestratorUrl.replace(/\/$/, '');
  const client = new OrchestratorClient(orchestratorUrl);
  const position = config.position ?? 'bottom-right';
  const zIndex = config.zIndex ?? 99999;
  const resizeHostPage = config.resizeHostPage !== false;
  /** Host page share when split; chat gets the remainder (30% by default). */
  const hostWidthPercent = config.hostWidthPercent ?? 70;
  const widgetWidthPercent = 100 - hostWidthPercent;
  const allowFullscreen = config.allowFullscreen !== false;
  const mobileFullscreen = config.mobileFullscreen !== false;
  // tabletBreakpoint covers phones + tablets; mobileBreakpoint is kept only as a fallback default.
  const tabletBreakpoint = config.tabletBreakpoint ?? config.mobileBreakpoint ?? 1024;
  const assistantName = config.assistantName ?? DEFAULT_ASSISTANT_NAME;
  const cookieDomain = config.cookieDomain ?? inferCookieDomain();
  const persistState = config.persistState !== false;
  const paletteNames = PRESET_PALETTE_OPTIONS.map((option) => option.id);
  const storedPalette = persistState ? localStorage.getItem(PALETTE_STORAGE_KEY) : null;
  let paletteName =
    config.paletteName && paletteNames.includes(config.paletteName)
      ? config.paletteName
      : storedPalette && paletteNames.includes(storedPalette)
        ? storedPalette
        : 'default';
  let palette = resolvePalette(config.palette, paletteName);

  function pageContextPayload() {
    return { pageUrl: readPageUrl(config.pageUrl) };
  }

  const host = document.createElement('div');
  host.style.zIndex = String(zIndex);
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = widgetStyles;
  shadow.appendChild(style);

  const launcher = document.createElement('button');
  launcher.className = `launcher ${position}`;
  launcher.type = 'button';
  launcher.setAttribute('aria-label', `Open ${config.title ?? DEFAULT_TITLE}`);
  launcher.innerHTML = `<img class="launcher-logo" src="${TAI_LOGO_SRC}" alt="" width="56" height="56" />`;
  shadow.appendChild(launcher);

  const panel = document.createElement('section');
  panel.className = `panel hidden ${position}`;
  panel.innerHTML = `
    <div class="header">
      <img class="header-logo" src="${TAI_LOGO_SRC}" alt="" />
      <div class="header-text">
        <h2>${escapeHtml(config.title ?? DEFAULT_TITLE)}</h2>
        <p>${escapeHtml(config.subtitle ?? DEFAULT_SUBTITLE)}</p>
      </div>
      <div class="header-actions">
        <div class="palette-picker">
          <button type="button" class="palette-trigger" aria-haspopup="listbox" aria-expanded="false" aria-label="Color palette">
            <span class="palette-swatch" aria-hidden="true"></span>
            <span class="palette-trigger-label">Default</span>
            <span class="palette-caret" aria-hidden="true">▾</span>
          </button>
          <div class="palette-menu" role="listbox" hidden></div>
        </div>
        <button type="button" class="icon-btn theme-toggle" aria-label="Switch to dark mode" title="Dark mode">☽</button>
        ${allowFullscreen ? '<button type="button" class="icon-btn fullscreen-toggle" aria-label="Enter fullscreen" title="Fullscreen">⛶</button>' : ''}
        <button type="button" class="icon-btn close-btn" aria-label="Close chat" title="Close">✕</button>
      </div>
    </div>
    <div class="messages" part="messages"></div>
    <form class="input-row" part="form">
      <input type="text" placeholder="Ask a question..." autocomplete="off" maxlength="2000" />
      <button type="submit">Send</button>
    </form>
  `;
  shadow.appendChild(panel);

  const messagesEl = panel.querySelector('.messages') as HTMLDivElement;
  const form = panel.querySelector('form') as HTMLFormElement;
  const input = panel.querySelector('input') as HTMLInputElement;
  const sendButton = form.querySelector('button') as HTMLButtonElement;
  const fullscreenToggle = panel.querySelector('.fullscreen-toggle') as HTMLButtonElement | null;
  const closeButton = panel.querySelector('.close-btn') as HTMLButtonElement;
  const themeToggle = panel.querySelector('.theme-toggle') as HTMLButtonElement;
  const palettePicker = panel.querySelector('.palette-picker') as HTMLDivElement;
  const paletteTrigger = panel.querySelector('.palette-trigger') as HTMLButtonElement;
  const paletteTriggerSwatch = panel.querySelector('.palette-swatch') as HTMLSpanElement;
  const paletteTriggerLabel = panel.querySelector('.palette-trigger-label') as HTMLSpanElement;
  const paletteMenu = panel.querySelector('.palette-menu') as HTMLDivElement;
  const subtitleEl = panel.querySelector('.header-text p') as HTMLParagraphElement;
  const processingStatus = createProcessingStatus(messagesEl, assistantName);
  const defaultSubtitle = config.subtitle ?? DEFAULT_SUBTITLE;

  // Prefer the shared cross-app cookie (if any) over the per-origin localStorage copy, so a
  // visitor moving from one app on the domain (e.g. dcp.) to another (e.g. ecp.) resumes the
  // same conversation instead of starting a new one.
  let conversationId = readConversationCookie() ?? localStorage.getItem(STORAGE_KEY) ?? undefined;
  if (conversationId && persistState) {
    localStorage.setItem(STORAGE_KEY, conversationId);
    writeConversationCookie(conversationId, cookieDomain);
  }
  // Desktop always uses the 30% split when open; compact viewports use fullscreen (or floating fallback).
  let isMaximized = !isCompactViewport();
  let isFullscreen = localStorage.getItem(FULLSCREEN_STORAGE_KEY) === 'true';
  let fullscreenForcedByMobile = false;
  let mobileLayoutMediaQuery: MediaQueryList | undefined;
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  let themePreference: WidgetTheme = isWidgetTheme(storedTheme)
    ? storedTheme
    : (config.theme ?? 'system');
  let pendingConfirmationId: string | undefined;
  let isBusy = false;
  let themeMediaQuery: MediaQueryList | undefined;
  let transcript: Array<{ role: MessageRole; text: string }> = [];
  let historyHydrated = false;

  function transcriptKey(): string {
    return `${TRANSCRIPT_KEY_PREFIX}${conversationId ?? 'anonymous'}`;
  }

  function saveTranscript() {
    if (!persistState) {
      return;
    }
    try {
      const payload = JSON.stringify(transcript);
      localStorage.setItem(transcriptKey(), payload);
      localStorage.setItem(
        LATEST_TRANSCRIPT_KEY,
        JSON.stringify({ conversationId: conversationId ?? null, messages: transcript }),
      );
    } catch {
      // Storage unavailable or full — the conversation still works for this page view.
    }
  }

  function loadTranscript(key: string): Array<{ role: MessageRole; text: string }> {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (entry): entry is { role: MessageRole; text: string } =>
          entry && isMessageRole(entry.role) && typeof entry.text === 'string',
      );
    } catch {
      return [];
    }
  }

  function loadLatestTranscript(): {
    conversationId?: string;
    messages: Array<{ role: MessageRole; text: string }>;
  } | null {
    try {
      const raw = localStorage.getItem(LATEST_TRANSCRIPT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        conversationId?: string | null;
        messages?: unknown;
      };
      if (!Array.isArray(parsed.messages)) return null;
      const messages = parsed.messages.filter(
        (entry): entry is { role: MessageRole; text: string } =>
          !!entry &&
          typeof entry === 'object' &&
          isMessageRole((entry as { role?: unknown }).role) &&
          typeof (entry as { text?: unknown }).text === 'string',
      );
      return {
        conversationId: parsed.conversationId || undefined,
        messages,
      };
    } catch {
      return null;
    }
  }

  function persistPanelOpen(isOpen: boolean) {
    if (!persistState) {
      return;
    }
    try {
      localStorage.setItem(PANEL_OPEN_STORAGE_KEY, String(isOpen));
    } catch {
      // ignore
    }
  }

  /** Sets the conversation id, mirrors it to localStorage + cookie, and migrates any
   * transcript saved under the old (often 'anonymous') key so history isn't dropped. */
  function setConversationId(newId: string | undefined) {
    if (!newId || newId === conversationId) return;
    const oldKey = transcriptKey();
    conversationId = newId;
    if (persistState) {
      localStorage.setItem(STORAGE_KEY, conversationId);
      writeConversationCookie(conversationId, cookieDomain);
    }
    const newKey = transcriptKey();
    if (oldKey !== newKey && persistState) {
      try {
        localStorage.setItem(newKey, JSON.stringify(transcript));
        localStorage.removeItem(oldKey);
        localStorage.setItem(
          LATEST_TRANSCRIPT_KEY,
          JSON.stringify({ conversationId, messages: transcript }),
        );
      } catch {
        // ignore — non-fatal
      }
    }
  }

  /** Rehydrates prior messages for this conversation: local cache first, then (best-effort) the
   * orchestrator's history endpoint for the cross-app case where only a shared cookie carried
   * over. Returns true if any history was restored, so callers can skip the welcome message. */
  async function hydrateHistory(): Promise<boolean> {
    if (historyHydrated) {
      return transcript.length > 0;
    }
    historyHydrated = true;

    let stored = loadTranscript(transcriptKey());

    if (!stored.length) {
      const latest = loadLatestTranscript();
      if (latest?.messages.length) {
        if (
          !conversationId ||
          !latest.conversationId ||
          latest.conversationId === conversationId
        ) {
          stored = latest.messages;
          if (latest.conversationId) {
            setConversationId(latest.conversationId);
          }
        }
      }
    }

    if (!stored.length && conversationId) {
      try {
        const remote = await client.getHistory(conversationId);
        stored = (remote.messages ?? []).filter(
          (entry): entry is HistoryMessage => isMessageRole(entry.role) && typeof entry.text === 'string',
        );
      } catch {
        // Orchestrator may not implement /orchestrator/history yet — degrade silently.
      }
    }

    for (const entry of stored) {
      appendMessage(entry.role, entry.text, { persist: false });
    }
    transcript = stored;
    if (stored.length) {
      saveTranscript();
    }
    return transcript.length > 0;
  }

  function closePaletteMenu() {
    paletteMenu.hidden = true;
    paletteTrigger.setAttribute('aria-expanded', 'false');
  }

  function openPaletteMenu() {
    paletteMenu.hidden = false;
    paletteTrigger.setAttribute('aria-expanded', 'true');
  }

  function syncPalettePicker() {
    const option = PRESET_PALETTE_OPTIONS.find((entry) => entry.id === paletteName) ?? PRESET_PALETTE_OPTIONS[0];
    paletteTriggerSwatch.style.background = option?.swatch ?? '#0039a7';
    paletteTriggerLabel.textContent = option?.label ?? 'Default';
    paletteTrigger.title = `Palette: ${option?.label ?? paletteName}`;
    paletteTrigger.setAttribute(
      'aria-label',
      `Color palette (current: ${option?.label ?? paletteName})`,
    );
    paletteMenu.querySelectorAll<HTMLButtonElement>('[data-palette]').forEach((btn) => {
      const selected = btn.dataset.palette === paletteName;
      btn.setAttribute('aria-selected', String(selected));
      btn.classList.toggle('is-selected', selected);
    });
  }

  for (const option of PRESET_PALETTE_OPTIONS) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'palette-option';
    item.role = 'option';
    item.dataset.palette = option.id;
    item.innerHTML = `<span class="palette-swatch" style="background:${option.swatch}" aria-hidden="true"></span><span>${escapeHtml(option.label)}</span>`;
    item.addEventListener('click', () => {
      setPaletteName(option.id);
      closePaletteMenu();
    });
    paletteMenu.appendChild(item);
  }

  function applyResolvedTheme(resolved: ResolvedTheme) {
    host.dataset.theme = resolved;
    applyPaletteColors(host, palette[resolved]);
    themeToggle.textContent = resolved === 'dark' ? '☀' : '☽';
    themeToggle.setAttribute(
      'aria-label',
      resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
    );
    themeToggle.title = resolved === 'dark' ? 'Light mode' : 'Dark mode';
    syncPalettePicker();
  }

  function applyThemePreference() {
    applyResolvedTheme(resolveTheme(themePreference));
    localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }

  function setThemePreference(theme: WidgetTheme) {
    themePreference = theme;
    applyThemePreference();
  }

  function setPaletteName(nextName: string) {
    paletteName = paletteNames.includes(nextName) ? nextName : 'default';
    palette = resolvePalette(config.palette, paletteName);
    if (persistState) {
      try {
        localStorage.setItem(PALETTE_STORAGE_KEY, paletteName);
      } catch {
        // ignore
      }
    }
    applyResolvedTheme(resolveTheme(themePreference));
  }

  applyThemePreference();

  const onSystemThemeChange = () => {
    if (themePreference === 'system') {
      applyResolvedTheme(resolveTheme('system'));
    }
  };

  if (typeof window !== 'undefined') {
    themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    themeMediaQuery.addEventListener('change', onSystemThemeChange);
  }

  /** True for phones and tablets (viewport <= tabletBreakpoint). */
  function isCompactViewport(): boolean {
    return window.matchMedia(`(max-width: ${tabletBreakpoint}px)`).matches;
  }

  function shouldUseMobileFullscreen(): boolean {
    return allowFullscreen && mobileFullscreen && isCompactViewport();
  }

  function syncHostLayout() {
    if (!resizeHostPage || isFullscreen) {
      disableHostSplit();
      return;
    }

    const shouldSplit = isMaximized && !panel.classList.contains('hidden');
    if (shouldSplit) {
      enableHostSplit(position, hostWidthPercent);
    } else {
      disableHostSplit();
    }
  }

  function applyPanelSize() {
    panel.classList.toggle('fullscreen', isFullscreen);
    panel.classList.toggle('maximized', isMaximized && !isFullscreen);
    host.dataset.fullscreen = isFullscreen ? 'true' : 'false';
    host.dataset.panelOpen = panel.classList.contains('hidden') ? 'false' : 'true';
    panel.style.setProperty('--orca-expanded-width', `${widgetWidthPercent}vw`);

    if (fullscreenToggle) {
      fullscreenToggle.textContent = isFullscreen ? '⤡' : '⛶';
      fullscreenToggle.setAttribute(
        'aria-label',
        isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen',
      );
      fullscreenToggle.title = isFullscreen ? 'Exit fullscreen' : 'Fullscreen';
      localStorage.setItem(FULLSCREEN_STORAGE_KEY, String(isFullscreen));
    }

    syncHostLayout();
  }

  function enterFullscreen(options?: { forcedByMobile?: boolean }) {
    if (isFullscreen) {
      return;
    }
    isFullscreen = true;
    isMaximized = false;
    fullscreenForcedByMobile = options?.forcedByMobile === true;
    applyPanelSize();
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function exitFullscreen() {
    if (!isFullscreen) {
      return;
    }
    isFullscreen = false;
    fullscreenForcedByMobile = false;
    // Always return to the 30% split on desktop (never the floating panel).
    isMaximized = !isCompactViewport();
    applyPanelSize();
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function toggleFullscreen() {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }

  function closePanel() {
    closePaletteMenu();
    panel.classList.add('hidden');
    host.dataset.panelOpen = 'false';
    if (isFullscreen) {
      isFullscreen = false;
      fullscreenForcedByMobile = false;
      localStorage.setItem(FULLSCREEN_STORAGE_KEY, 'false');
    }
    isMaximized = !isCompactViewport();
    applyPanelSize();
    persistPanelOpen(false);
  }

  const onMobileLayoutChange = () => {
    if (panel.classList.contains('hidden')) {
      isMaximized = !isCompactViewport();
      applyPanelSize();
      return;
    }
    if (shouldUseMobileFullscreen()) {
      if (!isFullscreen) {
        enterFullscreen({ forcedByMobile: true });
      }
      return;
    }
    if (fullscreenForcedByMobile) {
      exitFullscreen();
    } else {
      isMaximized = !isCompactViewport();
      applyPanelSize();
    }
  };

  if (typeof window !== 'undefined') {
    mobileLayoutMediaQuery = window.matchMedia(`(max-width: ${tabletBreakpoint}px)`);
    mobileLayoutMediaQuery.addEventListener('change', onMobileLayoutChange);
  }

  applyPanelSize();

  function formatOfferPrice(offer: DomainOffer): string {
    if (offer.firstYearPrice === undefined || offer.firstYearPrice === null || offer.firstYearPrice === '') {
      return '';
    }
    const amount =
      typeof offer.firstYearPrice === 'number'
        ? offer.firstYearPrice.toFixed(2)
        : String(offer.firstYearPrice);
    return offer.currency?.toUpperCase() === 'USD' || !offer.currency ? `$${amount}/yr` : `${offer.currency} ${amount}/yr`;
  }

  function appendMessage(role: MessageRole, text: string, options?: { persist?: boolean }) {
    const bubble = document.createElement('div');
    bubble.className = `message ${role}`;
    if (role === 'assistant' || role === 'system') {
      bubble.classList.add('rich');
      bubble.innerHTML = renderRichText(text);
    } else {
      bubble.textContent = text;
    }
    messagesEl.appendChild(bubble);
    messagesEl.scrollTop = messagesEl.scrollHeight;

    if (options?.persist !== false) {
      transcript.push({ role, text });
      if (transcript.length > MAX_STORED_MESSAGES) {
        transcript = transcript.slice(-MAX_STORED_MESSAGES);
      }
      saveTranscript();
    }
  }

  function buildWelcomeMessage(session: SessionResponse | null): string {
    if (session?.greeting) {
      return `${session.greeting}! I'm TAI, your Turbify AI assistant. Ask about domains, billing, or account help.`;
    }
    return "Hello! I'm TAI, your Turbify AI assistant. Sign in for account tasks, or ask about domains and billing.";
  }

  async function refreshSessionGreeting(): Promise<SessionResponse | null> {
    try {
      const session = await client.getSession(conversationId);
      setConversationId(session.conversationId);
      if (session.authenticated && session.greeting) {
        subtitleEl.textContent = `${session.greeting} — how can I help you today?`;
      } else {
        subtitleEl.textContent = defaultSubtitle;
      }
      return session;
    } catch {
      subtitleEl.textContent = defaultSubtitle;
      return null;
    }
  }

  async function openPanel(showWelcome: boolean) {
    panel.classList.remove('hidden');
    host.dataset.panelOpen = 'true';
    persistPanelOpen(true);
    if (shouldUseMobileFullscreen() && !isFullscreen) {
      enterFullscreen({ forcedByMobile: true });
    } else {
      syncHostLayout();
    }
    input.focus();
    const [hadHistory, session] = await Promise.all([hydrateHistory(), refreshSessionGreeting()]);
    if (showWelcome && !hadHistory && messagesEl.childElementCount === 0) {
      appendMessage('system', buildWelcomeMessage(session));
    }
  }

  function clearConfirmActions() {
    shadow.querySelectorAll('.confirm-actions').forEach((node) => node.remove());
    pendingConfirmationId = undefined;
  }

  function clearChannelActions() {
    shadow.querySelectorAll('.channel-actions').forEach((node) => node.remove());
  }

  function clearDomainCartActions() {
    shadow.querySelectorAll('.domain-offer-actions, .domain-confirm').forEach((node) => node.remove());
  }

  /** Clears every pending confirmation/channel/domain-cart prompt — used by the per-flow Cancel
   * buttons, the Escape key, and the public `cancelActiveFlow()` API to back out of any in-flight
   * flow without affecting the rest of the conversation. */
  function cancelActiveFlow(message = 'Okay, cancelled.') {
    const hadPending = hasPendingFlow();
    clearConfirmActions();
    clearChannelActions();
    clearDomainCartActions();
    if (hadPending) {
      appendMessage('system', message);
    }
  }

  function hasPendingFlow(): boolean {
    return Boolean(
      pendingConfirmationId ||
        shadow.querySelector('.channel-actions') ||
        shadow.querySelector('.domain-offer-actions') ||
        shadow.querySelector('.domain-confirm'),
    );
  }

  function showDomainCartActions(offers: DomainOffer[]) {
    clearDomainCartActions();
    const actionable = offers.filter((offer) => offer.available);
    if (!actionable.length) {
      return;
    }

    const actions = document.createElement('div');
    actions.className = 'domain-offer-actions';

    for (const offer of actionable) {
      const row = document.createElement('div');
      row.className = 'domain-offer-row';

      const label = document.createElement('div');
      label.className = 'domain-offer-label';
      const price = formatOfferPrice(offer);
      label.innerHTML = `<strong>${escapeHtml(offer.domain)}</strong>${price ? `<span>${escapeHtml(price)}</span>` : ''}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'domain-add-cart';
      button.textContent = 'Add to cart';
      button.addEventListener('click', () => {
        confirmDomainAddToCart(offer.domain);
      });

      row.appendChild(label);
      row.appendChild(button);
      actions.appendChild(row);
    }

    const dismissRow = document.createElement('div');
    dismissRow.className = 'domain-offer-dismiss';
    const dismissButton = document.createElement('button');
    dismissButton.type = 'button';
    dismissButton.className = 'link-cancel';
    dismissButton.textContent = 'No thanks';
    dismissButton.addEventListener('click', () => cancelActiveFlow('Okay, not adding a domain right now.'));
    dismissRow.appendChild(dismissButton);
    actions.appendChild(dismissRow);

    messagesEl.appendChild(actions);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  /** Confirmation gate in front of the actual add-to-cart call, so nothing is added to the cart
   * without an explicit Confirm click (and Cancel always backs out cleanly). */
  function confirmDomainAddToCart(domainName: string) {
    clearDomainCartActions();

    const prompt = document.createElement('div');
    prompt.className = 'confirm-actions domain-confirm';
    prompt.innerHTML = `
      <div class="confirm-copy">Add <strong>${escapeHtml(domainName)}</strong> to your cart?</div>
      <div class="confirm-buttons">
        <button type="button" class="confirm">Confirm</button>
        <button type="button" class="cancel">Cancel</button>
      </div>
    `;

    prompt.querySelector('.confirm')?.addEventListener('click', () => {
      prompt.remove();
      void handleDomainAddToCart(domainName);
    });
    prompt.querySelector('.cancel')?.addEventListener('click', () => {
      prompt.remove();
      appendMessage('system', `Cancelled adding ${domainName} to cart.`);
    });

    messagesEl.appendChild(prompt);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function handleDomainAddToCart(domainName: string) {
    if (isBusy) return;
    isBusy = true;
    sendButton.disabled = true;
    clearDomainCartActions();
    const userLine = `Add ${domainName} to my cart`;
    appendMessage('user', userLine);
    processingStatus.show();

    try {
      const response = await client.chat({
        message: userLine,
        conversationId,
        inputs: { domainName, term: '12' },
        ...pageContextPayload(),
      });

      setConversationId(response.conversationId);
      handleAssistantResponse(response);
    } catch (error) {
      appendMessage('error', error instanceof Error ? error.message : 'Add to cart failed.');
    } finally {
      processingStatus.hide();
      isBusy = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  function showChannelActions(channels: RecoveryChannelOption[]) {
    clearChannelActions();
    if (!channels.length) {
      return;
    }

    const actions = document.createElement('div');
    actions.className = 'channel-actions';

    for (const channel of channels) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = channelButtonLabel(channel);
      button.addEventListener('click', () => {
        void handleChannelSelect(channel.channelRef, channelButtonLabel(channel));
      });
      actions.appendChild(button);
    }

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'channel-cancel';
    cancelButton.textContent = 'Cancel';
    cancelButton.addEventListener('click', () => cancelActiveFlow('Account recovery cancelled.'));
    actions.appendChild(cancelButton);

    messagesEl.appendChild(actions);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function handleChannelSelect(resetType: string, label: string) {
    if (isBusy) return;
    isBusy = true;
    sendButton.disabled = true;
    clearChannelActions();
    appendMessage('user', label);
    processingStatus.show();

    try {
      const response = await client.chat({
        message: label,
        conversationId,
        inputs: { resetType },
        ...pageContextPayload(),
      });

      setConversationId(response.conversationId);
      appendMessage('assistant', formatResponse(response));
      maybeNavigate(response);
    } catch (error) {
      appendMessage('error', error instanceof Error ? error.message : 'Channel selection failed.');
    } finally {
      processingStatus.hide();
      isBusy = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  function handleAssistantResponse(response: ChatResponse) {
    appendMessage('assistant', formatResponse(response));

    if (response.domainOffers?.length) {
      showDomainCartActions(response.domainOffers);
    }

    if (response.requiresConfirmation && response.confirmationId) {
      showConfirmationActions(response.confirmationId);
    }

    if (response.requiresChannelSelection && response.recoveryChannels?.length) {
      showChannelActions(response.recoveryChannels);
    }

    maybeNavigate(response);
  }

  /** Navigates the host page when the orchestrator returns `navigateTo` (same tab by default). */
  function maybeNavigate(response: ChatResponse) {
    if (!response.navigateTo) {
      return;
    }

    const href = resolveNavigateUrl(response.navigateTo);
    if (!href) {
      return;
    }

    // Keep the widget open across the page load so history rehydrates on the destination.
    persistPanelOpen(true);
    saveTranscript();

    const mode = response.navigateMode ?? 'same-tab';
    if (mode === 'new-tab') {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    // Defer so the reply paints before the document unloads.
    window.setTimeout(() => {
      window.location.assign(href);
    }, 50);
  }

  function showConfirmationActions(confirmationId: string) {
    clearConfirmActions();
    pendingConfirmationId = confirmationId;

    const actions = document.createElement('div');
    actions.className = 'confirm-actions';
    actions.innerHTML = `
      <button type="button" class="confirm">Confirm</button>
      <button type="button" class="cancel">Cancel</button>
    `;

    actions.querySelector('.confirm')?.addEventListener('click', () => {
      void handleConfirm(confirmationId);
    });
    actions.querySelector('.cancel')?.addEventListener('click', () => {
      cancelActiveFlow('Action cancelled.');
    });

    messagesEl.appendChild(actions);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function handleConfirm(confirmationId: string) {
    if (isBusy) return;
    isBusy = true;
    sendButton.disabled = true;
    clearConfirmActions();
    processingStatus.show();

    try {
      const response = await client.confirm({
        confirmationId,
        ...pageContextPayload(),
      });
      setConversationId(response.conversationId);
      handleAssistantResponse(response);
    } catch (error) {
      appendMessage('error', error instanceof Error ? error.message : 'Confirmation failed.');
    } finally {
      processingStatus.hide();
      isBusy = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  async function handleSend(message: string) {
    if (isBusy || !message.trim()) return;

    isBusy = true;
    sendButton.disabled = true;
    appendMessage('user', message.trim());
    input.value = '';
    processingStatus.show();

    try {
      const response = await client.chat({
        message: message.trim(),
        conversationId,
        ...pageContextPayload(),
      });

      setConversationId(response.conversationId);
      handleAssistantResponse(response);
    } catch (error) {
      appendMessage('error', error instanceof Error ? error.message : 'Something went wrong.');
    } finally {
      processingStatus.hide();
      isBusy = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  launcher.addEventListener('click', () => {
    const isOpen = !panel.classList.contains('hidden');
    if (isOpen) {
      closePanel();
      return;
    }
    void openPanel(true);
  });

  fullscreenToggle?.addEventListener('click', () => {
    toggleFullscreen();
  });

  closeButton.addEventListener('click', () => {
    closePanel();
  });

  const onDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || panel.classList.contains('hidden')) {
      return;
    }
    if (!paletteMenu.hidden) {
      closePaletteMenu();
      return;
    }
    if (isFullscreen) {
      exitFullscreen();
      return;
    }
    if (hasPendingFlow()) {
      cancelActiveFlow();
      return;
    }
    closePanel();
  };

  document.addEventListener('keydown', onDocumentKeydown);

  themeToggle.addEventListener('click', () => {
    const resolved = resolveTheme(themePreference);
    setThemePreference(resolved === 'dark' ? 'light' : 'dark');
  });

  paletteTrigger.addEventListener('click', (event) => {
    event.stopPropagation();
    if (paletteMenu.hidden) {
      openPaletteMenu();
    } else {
      closePaletteMenu();
    }
  });

  paletteMenu.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  panel.addEventListener('click', (event) => {
    if (!palettePicker.contains(event.target as Node)) {
      closePaletteMenu();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void handleSend(input.value);
  });

  /** Clears any pending flow, wipes the saved transcript, and starts a brand new conversation
   * (dropping the shared cookie too, so other apps on the domain also start fresh). */
  function resetConversation() {
    cancelActiveFlow();
    try {
      localStorage.removeItem(transcriptKey());
      localStorage.removeItem(LATEST_TRANSCRIPT_KEY);
    } catch {
      // ignore
    }
    localStorage.removeItem(STORAGE_KEY);
    clearConversationCookie(cookieDomain);
    conversationId = undefined;
    transcript = [];
    historyHydrated = true;
    messagesEl.innerHTML = '';
    void refreshSessionGreeting().then((session) => {
      appendMessage('system', buildWelcomeMessage(session));
    });
  }

  const api: OrcaChatWidgetInstance = {
    open: () => {
      void openPanel(messagesEl.childElementCount === 0);
    },
    close: () => {
      closePanel();
    },
    maximize: () => {
      if (isFullscreen) {
        exitFullscreen();
        return;
      }
      isMaximized = !isCompactViewport();
      applyPanelSize();
    },
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    isFullscreen: () => isFullscreen,
    setTheme: (theme: WidgetTheme) => {
      setThemePreference(theme);
    },
    getTheme: () => themePreference,
    setPaletteName,
    getPaletteName: () => paletteName,
    cancelActiveFlow: () => cancelActiveFlow(),
    resetConversation,
    destroy: () => {
      processingStatus.hide();
      document.removeEventListener('keydown', onDocumentKeydown);
      themeMediaQuery?.removeEventListener('change', onSystemThemeChange);
      mobileLayoutMediaQuery?.removeEventListener('change', onMobileLayoutChange);
      removeHostLayoutStyles();
      host.remove();
    },
  };

  // Restore open panel + history after full-page navigations on the same domain.
  if (persistState && localStorage.getItem(PANEL_OPEN_STORAGE_KEY) === 'true') {
    void openPanel(false);
  }

  return api;
}

/** Infers a parent cookie domain (e.g. `.turbify.com`) so history survives subdomain hops. */
function inferCookieDomain(): string | undefined {
  if (typeof location === 'undefined') {
    return undefined;
  }
  const host = location.hostname;
  if (!host || host === 'localhost' || host.endsWith('.localhost')) {
    return undefined;
  }
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':')) {
    return undefined;
  }
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) {
    return undefined;
  }
  return `.${parts.slice(-2).join('.')}`;
}

export function init(config: OrcaChatWidgetConfig): OrcaChatWidgetInstance {
  if (!config.orchestratorUrl) {
    throw new Error('orchestratorUrl is required');
  }
  return createWidget(config);
}

declare global {
  interface Window {
    OrcaChatWidget?: {
      init: typeof init;
    };
  }
}

if (typeof window !== 'undefined') {
  window.OrcaChatWidget = { init };
}
